/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';

import Loading from 'components/Loading/Loading';
import AutoCompleteModel from 'models/forms/AutoCompleteModel';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import { autoCompleteTradingAccountsForBrandAssignment } from 'api/forms';
import { createNewLocation } from 'permissions/account';
import { createNewCostCenter } from 'permissions/costCenter';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import navigation from 'services/NavigationService';
import BrandStore from 'stores/BrandStore';

import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ElectroFileTransfer from '../components/inputs/ElectroFileTransfer';
import FormControls from '../components/controls/FormControls/FormControls';

import { makeFields, brandCreate, brandEdit } from './setupBrandForm';

export type BrandFormProps = {
  mode: 'edit' | 'create',
  brandId?: string,
};

type InjectedProps = {
  brandStore: BrandStore,
} & TranslatorProps;

@translate()
@inject('brandStore', 'actionDrawerStore')
@observer
export default class BrandForm extends ComponentWithInjectedProps<
  BrandFormProps,
  {},
  InjectedProps
> {
  @observable accountsModel = new AutoCompleteModel(autoCompleteTradingAccountsForBrandAssignment);

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    firstLoad: true,
    hasSuccess: false,
    hasError: false,

    selectedTradingAccounts: '',
    duplicateBrand: null,
  };

  handleKeepEditing = () => {
    this.localState.duplicateBrand = null;
  };

  handleSwitchToDuplicateBrand = () => {
    const duplicateBrandId = this.localState.duplicateBrand && this.localState.duplicateBrand.id;
    this.localState.duplicateBrand = null;

    if (duplicateBrandId) {
      navigation.replace(`/brands/edit/${duplicateBrandId}`);
    }
  };

  @computed get form(): Object {
    const { mode, t, brandStore } = this.props;

    const fields = makeFields(t, mode, brandStore.selectedBrand);

    switch (mode) {
      case 'edit':
        return brandEdit(fields, brandStore.selectedBrand || {}, this.localState, this.props.t);

      case 'create':
      default:
        return brandCreate(fields, this.localState, this.props.t);
    }
  }

  constructor(props: BrandFormProps & InjectedProps) {
    super(props);

    const { brandStore, brandId, mode } = props;

    if (mode === 'edit') {
      if (!brandId) {
        throw new Error('Brand ID is required for Brand Edit form');
      }

      this.localState.isLoading = true;

      brandStore
        .loadBrand(brandId)
        .then(brand => {
          if (brand) {
            this.localState.selectedTradingAccounts = brand.accounts;
          }

          this.localState.isLoading = false;
        })
        .catch(err => {
          this.localState.showResponseMessage = true;
          this.localState.hasError = true;
          this.localState.responseMessage = err.message;
          this.localState.isLoading = false;
        });
    }
  }

  render() {
    const { mode, t } = this.props;
    const { duplicateBrand } = this.localState;

    return this.localState.isLoading && this.localState.firstLoad ? (
      <Loading />
    ) : (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <Dialog open={Boolean(duplicateBrand)} onClose={this.handleKeepEditing}>
          <DialogTitle>{t('general:reporting.brand_form.duplicate_name.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('general:reporting.brand_form.duplicate_name.message', {
                name: duplicateBrand && duplicateBrand.name,
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleKeepEditing}>
              {t('general:reporting.brand_form.duplicate_name.keep_editing')}
            </Button>
            <Button color="primary" onClick={this.handleSwitchToDuplicateBrand}>
              {t('general:reporting.brand_form.duplicate_name.switch_and_edit')}
            </Button>
          </DialogActions>
        </Dialog>
        <FormWrapper form={this.form}>
          <FormSection form={this.form} section="details">
            <ElectroTextField field={this.form.$('details.name')} />
          </FormSection>

          <FormSection form={this.form} section="brandLogo">
            <ElectroFileTransfer field={this.form.$('brandLogo.file')} variant="upload" />
          </FormSection>

          <FormSection form={this.form} section="accounts">
            <ElectroSelectExtra
              field={this.form.$('accounts.chooseAccounts')}
              form={this.form}
              formName="accounts"
              labelKey={['account_no', 'name']}
              labelSeparator=" - "
              multi
              singleSelectedValue={this.localState.selectedTradingAccounts}
              handleOnChange={newValue => {
                this.localState.selectedTradingAccounts = newValue;
              }}
              props={{
                model: this.accountsModel,
              }}
            />
          </FormSection>
          <FormControls
            form={this.form}
            buttonText={
              this.props.mode === 'edit' ? t('general:actions.save') : t('general:actions.create')
            }
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}
